/**
 * #272 — the two Position Tracker bulk-cancel SCOPES, as pure functions.
 *
 * Extracted from the view so the thing that decides "which orders does this
 * button destroy" is testable without mounting a 7,000-line page. The view keeps
 * only the freeze + confirm + execute wiring; the scope rules live here.
 *
 * The two scopes differ in exactly one way, and it is deliberate:
 *
 *   ALL     — the UNFILTERED own-scoped lists. Sport / market-family chips must
 *             never silently shrink an "everything" sweep; a trader signing off
 *             at end of day is asking about orders they cannot currently see
 *             (Kevin, Jun 2).
 *   VISIBLE — exactly the rendered rows. "Clear this slate, keep my other sports
 *             resting" (Dan, Jul 8).
 *
 * Both return plain targets. Freezing them into an immutable snapshot is the
 * caller's job, and it must happen when the confirmation OPENS — these builders
 * read live arrays by design, so calling one at confirm time would reintroduce
 * exactly the moving-set bug the snapshot exists to prevent.
 */

import type { OrderCancelTarget } from '@/lib/trading/gameOrderSelection';
import type { KalshiOrder } from '@/types/kalshi';
import type { PolyRelayOpenOrder } from '@/lib/polymarket/relay';

/** CLOB returns the order ID under one of three spellings depending on SDK
 *  version. An order with none of them cannot be cancelled individually, so it
 *  is dropped rather than turned into a DELETE against an empty path. */
export function polyOrderIdOf(order: PolyRelayOpenOrder): string {
  for (const value of [order.id, order.orderID, order.orderId]) {
    if (typeof value === 'string' && value.trim() !== '') return value.trim();
  }
  return '';
}

function kalshiTarget(orderId: string): OrderCancelTarget {
  return { key: `kalshi:${orderId}`, venue: 'kalshi', orderId };
}

function polyTarget(orderId: string): OrderCancelTarget {
  return { key: `polymarket:${orderId}`, venue: 'polymarket', orderId };
}

/** Every open order on both venues, ignoring the section's filters entirely. */
export function buildAllCancelTargets(args: {
  kalshiOrders: readonly KalshiOrder[];
  polyOrders: readonly PolyRelayOpenOrder[];
  /** Polymarket unavailable (no cred / not in org): its list is not authority,
   *  so it contributes nothing rather than a guess. */
  polyUnavailable?: boolean;
}): OrderCancelTarget[] {
  const targets: OrderCancelTarget[] = [];
  for (const order of args.kalshiOrders) {
    if (order.orderId) targets.push(kalshiTarget(order.orderId));
  }
  if (!args.polyUnavailable) {
    for (const order of args.polyOrders) {
      const id = polyOrderIdOf(order);
      if (id) targets.push(polyTarget(id));
    }
  }
  return targets;
}

/** A rendered row of the orders list — the view's discriminated union. */
export type VisibleOrderRow =
  | { kind: 'kalshi'; order: Pick<KalshiOrder, 'orderId'> }
  | { kind: 'poly'; order: PolyRelayOpenOrder };

/** Exactly the rows currently rendered, in render order. */
export function buildVisibleCancelTargets(
  rows: readonly VisibleOrderRow[],
): OrderCancelTarget[] {
  const targets: OrderCancelTarget[] = [];
  for (const row of rows) {
    if (row.kind === 'kalshi') {
      if (row.order.orderId) targets.push(kalshiTarget(row.order.orderId));
    } else {
      const id = polyOrderIdOf(row.order);
      if (id) targets.push(polyTarget(id));
    }
  }
  return targets;
}

/**
 * The scoped "Cancel visible" button only earns its slot when the filters are
 * actually narrowing the list — otherwise it silently duplicates "Cancel all"
 * and the trader has two buttons that do the same destructive thing.
 */
export function shouldOfferCancelVisible(args: {
  filtersActive: boolean;
  visibleCount: number;
  allCount: number;
}): boolean {
  return args.filtersActive && args.visibleCount > 0 && args.visibleCount < args.allCount;
}
